// TeamManagementSwitch - routes /team to either the legacy Team page or the
// card-based V2 experience, gated by a strict per-tenant feature flag.
//
// The flag is read as a DIRECT boolean off the company doc's featureFlags map
// (`companyData?.featureFlags?.team_management_v2 === true`). This is deliberate:
// it is NOT registered in FEATURE_REGISTRY and NOT resolved via hasFeature, so
// there is no tier fallback and no additive-override ambiguity. Absent or any
// non-true value renders the legacy page unchanged. There are no hardcoded
// company allowlists - enablement is per-tenant Firestore data only.

import React, { Suspense } from 'react';
import { useOutletContext } from 'react-router-dom';
import TeamManagementPage from './TeamManagementPage';
import { isTeamManagementV2Enabled } from './v2/constants';

// V2 is code-split so tenants on the legacy page never download the new bundle.
const TeamManagementV2Page = React.lazy(() => import('./v2/TeamManagementV2Page'));

interface SwitchOutletContext {
  companyData: { featureFlags?: Record<string, unknown> } | null;
}

const TeamManagementSwitch: React.FC = () => {
  const { companyData } = useOutletContext<SwitchOutletContext>();

  if (!isTeamManagementV2Enabled(companyData)) {
    return <TeamManagementPage />;
  }

  return (
    <Suspense
      fallback={
        <div className="min-h-[50vh] py-16 text-center text-sm text-gray-400">
          Loading team management…
        </div>
      }
    >
      <TeamManagementV2Page />
    </Suspense>
  );
};

export default TeamManagementSwitch;
